import React, { useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import servicesData from "./ServicesData";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import OptimizedImg from "../../components/OptimizedImg";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

/* Arrow Component using centered SVG icons */
const Arrow = ({ onClick, direction }) => (
  <button
    type="button"
    aria-label={direction === "left" ? "Previous slide" : "Next slide"}
    className={`service-arrow ${direction}`}
    onClick={onClick}
  >
    {direction === "left" ? (
      <ChevronLeft size={24} strokeWidth={2.5} />
    ) : (
      <ChevronRight size={24} strokeWidth={2.5} />
    )}
  </button>
);

const ServiceSlide = () => {
  const sliderRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const checkScreen = () => setIsMobile(window.innerWidth <= 768);
    checkScreen();
    window.addEventListener("resize", checkScreen);
    return () => window.removeEventListener("resize", checkScreen);
  }, []);

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToScroll: 1,
    slidesToShow: isMobile ? 1 : 3,
    centerMode: !isMobile,
    centerPadding: "0px",
    prevArrow: <Arrow direction="left" />,
    nextArrow: <Arrow direction="right" />,
    afterChange: (index) => setCurrent(index),
  };

  return (
    <div className="service-slider-section">
      {/* ===== TITLE AREA ===== */}
      <div className="course-header">
        <div className="badge-center">
          <span className="section-badge">What We Offer</span>
        </div>
        <h2>Our Services</h2>
        <p>Industry-focused solutions built for growth</p>
      </div>

      {/* ===== SLIDER ===== */}
      <div className="service-slider-wrapper">
        <Slider ref={sliderRef} {...settings}>
          {servicesData.map((service, index) => {
            const isCenter = index === current;
            const isEdtech = service.slug === "edtech-solutions";
            const linkHref = isEdtech ? "/courses" : `/services/${service.slug}`;

            const handleClick = (e) => {
              if (!isCenter) {
                e.preventDefault();
                if (sliderRef.current) sliderRef.current.slickGoTo(index);
                return;
              }
              if (isEdtech) return;
              window.scrollTo(0, 0);
            };

            const linkProps = isEdtech
              ? { href: linkHref, target: "_blank", rel: "noopener noreferrer" }
              : { to: linkHref };

            const LinkTag = isEdtech ? "a" : Link;

            const card = (
              <div className="service-card">
                <div className="service-image-wrap">
                  <OptimizedImg
                    src={service.image}
                    alt={service.name}
                    className="service-image"
                    width={800}
                    height={220}
                    loading={index < 3 || index >= servicesData.length - 3 ? "eager" : "lazy"}
                    decoding="async"
                  />
                  <div className="service-image-overlay"></div>
                </div>
                <div className="service-content">
                  <div>
                    <h3>{service.name}</h3>
                    <p>{service.description.slice(0, 120)}...</p>
                  </div>
                  <div className="read-more-text">
                    Read More
                    <span className="read-more-arrow">→</span>
                  </div>
                </div>
              </div>
            );

            return (
              <div key={service.slug}>
                <LinkTag
                  {...linkProps}
                  onClick={handleClick}
                  style={{ textDecoration: "none", cursor: !isCenter ? "pointer" : "default" }}
                >
                  {card}
                </LinkTag>
              </div>
            );
          })}
        </Slider>
      </div>

      {/* ===== STYLES ===== */}
      <style>{`
        .service-slider-section {
          padding: 80px 0;
          background-image: url('/images/background/b.png');
          background-image: image-set(url('/images/background/b.avif') type('image/avif'), url('/images/background/b.webp') type('image/webp'), url('/images/background/b.png') type('image/png'));
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
        }
        @media (max-width: 768px) {
          .service-slider-section {
            background-image: url('/images/background/b.png');
            background-image: image-set(url('/images/background/b-960.avif') type('image/avif'), url('/images/background/b-960.webp') type('image/webp'), url('/images/background/b.png') type('image/png'));
          }
        }

        .course-header {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: left;
          max-width: 100%;
          margin: 0 0 50px;
          padding: 0 70px;
        }

        .badge-center {
          text-align: center;
          width: 100%;
          margin-bottom: 14px;
        }

        .section-badge {
          display: inline-block;
          background: #efe9fb;
          color: #6c0a9f;
          font-size: 13px;
          font-weight: 600;
          padding: 6px 16px;
          border-radius: 30px;
          letter-spacing: 0.3px;
        }

        .course-header h2 {
          font-family: 'Outfit', sans-serif;
          font-size: clamp(2.1rem, 4vw, 3rem);
          font-weight: 800;
          line-height: 1.15;
          color: #14142b;
          margin: 0 0 12px;
          align-self: flex-start;
          text-align: left;
        }

        .course-header p {
          font-family: 'Inter', sans-serif;
          font-size: 15.5px;
          font-weight: 400;
          line-height: 1.75;
          color: #666;
          margin: 0;
          max-width: 520px;
          align-self: flex-start;
          text-align: left;
        }

        .service-slider-wrapper {
          position: relative;
          padding: 0 70px;
        }

        .service-card {
          background: #fff;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 8px 24px rgba(20, 20, 43, 0.08);
          border: 1px solid rgba(20, 20, 43, 0.06);
          transition: all 0.35s ease;
          display: flex;
          flex-direction: column;
          margin: 10px;
        }

        .service-card:hover {
          box-shadow: 0 16px 36px rgba(108, 10, 159, 0.16);
          transform: translateY(-4px);
        }

        .service-image-wrap {
          position: relative;
          overflow: hidden;
        }

        .service-image-wrap picture {
          display: block;
          width: 100%;
        }

        .service-image {
          width: 100%;
          height: 220px;
          object-fit: cover;
          display: block;
          transition: transform 0.5s ease;
        }

        .service-card:hover .service-image {
          transform: scale(1.06);
        }

        .service-image-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(20,20,43,0) 55%, rgba(20,20,43,0.35) 100%);
        }

        .service-content {
          padding: 22px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          flex: 1;
          gap: 16px;
        }

        .service-content h3 {
          font-family: 'Outfit', sans-serif;
          color: #14142b;
          margin-bottom: 8px;
          font-weight: 700;
          font-size: 19px;
        }

        .service-content p {
          font-family: 'Inter', sans-serif;
          color: #666;
          font-size: 14.5px;
          line-height: 1.6;
          margin: 0;
        }

        .read-more-text {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          align-self: flex-start;
          font-weight: 600;
          color: #6c0a9f;
          font-size: 14px;
          transition: gap 0.25s ease;
        }

        .service-card:hover .read-more-text {
          gap: 10px;
        }

        .read-more-arrow {
          font-size: 15px;
        }

        /* Perfectly Centered Circular Arrow Button (No native borders/outlines) */
        .service-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 46px;
          height: 46px;
          padding: 0;
          margin: 0;
          border: none;
          outline: none;
          background: #ffffff;
          border-radius: 50%;
          box-shadow: 0 4px 14px rgba(108, 10, 159, 0.18);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 100;
          color: #6c0a9f;
          -webkit-tap-highlight-color: transparent;
          transition: background-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease, color 0.25s ease;
        }

        .service-arrow:focus,
        .service-arrow:active,
        .service-arrow:focus-visible {
          outline: none !important;
          border: none !important;
          box-shadow: 0 4px 14px rgba(108, 10, 159, 0.18);
        }

        .service-arrow svg {
          display: block;
          flex-shrink: 0;
        }

        .service-arrow:hover {
          background: #6c0a9f;
          border: none;
          color: #ffffff;
          box-shadow: 0 8px 22px rgba(108, 10, 159, 0.35);
          transform: translateY(-50%) scale(1.06);
        }

        .service-arrow.left { 
          left: 14px; 
        }

        .service-arrow.right { 
          right: 14px; 
        }

        .slick-center .service-card {
          transform: scale(1);
          opacity: 1;
          filter: blur(0);
        }

        .slick-slide:not(.slick-center) .service-card {
          transform: scale(0.9);
          opacity: 0.55;
          filter: blur(1px);
        }

        @media (max-width: 768px) {
          .service-slider-section { padding: 60px 0; }

          .course-header { padding: 0 40px; }

          .course-header h2 { font-size: clamp(1.7rem, 7vw, 2.1rem); }

          .service-slider-wrapper { padding: 0 40px; }

          .service-arrow {
            width: 38px;
            height: 38px;
          }

          .service-arrow svg {
            width: 20px;
            height: 20px;
          }

          .service-arrow.left { left: 4px; }
          .service-arrow.right { right: 4px; }

          .slick-slide .service-card {
            transform: scale(1) !important;
            opacity: 1 !important;
            filter: none !important;
            max-width: 320px;
            margin: 10px auto;
          }
        }
      `}</style>
    </div>
  );
};

export default ServiceSlide;